
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContentText,
    DialogActions,
    DialogContent,
    Slide,
    Paper,
    FormControl,
    InputLabel,
    Select,
    Autocomplete,
    Stack,
    Badge,
    TextField,
    Tooltip,
    Collapse,
    Divider
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import PushPinIcon from "@mui/icons-material/PushPin";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ListIcon from "@mui/icons-material/List";
import {useTheme} from '@mui/material/styles';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import Config from "../../config";
import { useAuth } from "../../hooks/useAuth";
import {ListViewModel,
        ListViewColumnModel,
        ListViewColumnFactory} from '../../models/models';
import {BaseSuccessModel} from '../../models/modelsException';
import {BarChartUIPreferenceModel,
        BarChartUIPreferenceFactory} from '../../models/modelsUserConfiguration';


type DatetimeWidthPicklist = {
  key: string;
  value: string;
}
const DATETIME_CONFIG: DatetimeWidthPicklist[] = [
  {key: 'hourOfDay', value: 'Hour of Day'},
  {key: 'weekOfYear', value: 'Week of Year'},
  {key: 'monthOfYear', value: 'Month of Year'},
  {key: 'weekDay', value: 'Weekday'},
  {key: 'day', value: 'Day'},
  {key: 'week', value: 'Week'},
  {key: 'month', value: 'Month'},
  {key: 'quarter', value: 'Quarter'},
  {key: 'year', value: 'Year'}
]

export const BarChartConfigurationMenuComponent: React.FC<{
  listView: ListViewModel,
  anchorEl: null | HTMLElement,
  onClose: () => void,
  onSubmit: (listView: ListViewModel, barChartGroups: BarChartUIPreferenceModel[]) => void
}> = ({
  listView,
  anchorEl,
  onClose,
  onSubmit
}) => {

  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();
  const {accessToken, onInvalidToken} = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const [barChartGroups, setBarChartGroups] = useState<BarChartUIPreferenceModel[]>([]);

  useEffect(() => {
    if(listView.user_preference?.barChartGroups){
      setBarChartGroups(listView.user_preference.barChartGroups.map((thisGroup) => thisGroup))
    }
  }, [listView])

  const submitBarChart = () => {
    onSubmit(listView, barChartGroups)
  }

  const resetBarChart = () => {
    setBarChartGroups([])
  }

  const addOrRemoveBarChartGroup = (column: ListViewColumnModel, matchedBarChartGroup: BarChartUIPreferenceModel | undefined) => {
    if(matchedBarChartGroup){
      //filter out from list
      setBarChartGroups((prev) => prev.filter((thisGroup) => thisGroup.groupColumnUuid !== column.Uuid_sys));
    } else {
      const newGroup = new ListViewColumnFactory().toBarChartGroup(column)
      if(column.ReferencedField_sys.Type_sys === 'DateTimeField'){
        //set current barchart group
        //because this requires to set a width
        newGroup.Width_sys='quarter'

      }
      setBarChartGroups((prev) => [...prev, newGroup])
    }
  }

  const setBarChartGroupConfig = (column: ListViewColumnModel, matchedBarChartGroup: BarChartUIPreferenceModel | undefined, newWidth: string) => {
    //no need to overwrite
    if(matchedBarChartGroup?.Width_sys === newWidth) return
    if(barChartGroups.length < 1) return
    if(matchedBarChartGroup){
      const mutableBarChartGroups = [...barChartGroups];
      mutableBarChartGroups[0].Width_sys = newWidth;
      setBarChartGroups(mutableBarChartGroups)
    }
  }

  const barChartMenuElement = (thisColumn: ListViewColumnModel) => {

    const matchedBarChartGroup = barChartGroups.find((thisBarChartGroup) => thisBarChartGroup.groupColumnUuid === thisColumn.Uuid_sys)

    const disableMenuItem = () => {
      //for now, only allow exactly one group
      //
      //first: deselect is always allowed
      if(matchedBarChartGroup) return false
      if(barChartGroups.length === 1) return true
      return false
    }

    const openSubMenu = () => {
      //sub menu to select width of the bar chart
      if(!matchedBarChartGroup) return false
      if(thisColumn.ReferencedField_sys.Type_sys !== 'DateTimeField') return false
      return true
    }

    return (
      <>
      <MenuItem
         key={thisColumn.Uuid_sys}
         onClick={() => addOrRemoveBarChartGroup(thisColumn, matchedBarChartGroup)}
         disabled={disableMenuItem()}
         sx={{
           justifyContent: 'space-between',
           backgroundColor: matchedBarChartGroup === undefined ? 'inherit' : 'primary.main'
         }}
      >
         <Typography
            sx={{ textAlign: 'center' }}
         >
           {thisColumn.ReferencedField_sys.Label_sys}
         </Typography>
      </MenuItem>
      <Collapse in={openSubMenu()} timeout="auto" unmountOnExit>
        {
          DATETIME_CONFIG.map((thisDatetimeConfig: DatetimeWidthPicklist) => {
            return(
              <MenuItem
                key={`${thisColumn.Uuid_sys}-${thisDatetimeConfig.key}`}
                sx={{
                  ml: 4,
                  backgroundColor: matchedBarChartGroup?.Width_sys === thisDatetimeConfig.key ? 'primary.main' : 'inherit',
                  cursor: matchedBarChartGroup?.Width_sys === thisDatetimeConfig.key ? 'default': 'pointer',
                  pointerEvents: matchedBarChartGroup?.Width_sys === thisDatetimeConfig.key ? 'none' : 'auto'
                //note: cannot use disabled here,
                //because greyed-out looks odd
                //workaround: explicitly set cursor and pointerEvents
                }}
                onClick={() => setBarChartGroupConfig(thisColumn, matchedBarChartGroup, thisDatetimeConfig.key)}
              >
                <Typography
                  color='text.secondary'
                  sx={{
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                >
                  {thisDatetimeConfig.value}
                </Typography>
              </MenuItem>
            )
          })
        }
        <Divider/>
       </Collapse>
       </>

    )
  }
  const ITEM_HEIGHT=48;

  return (
      <Menu
        id="menu-barchart"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
        sx={{ display: { xs: 'block' }}}
        slotProps = {{
          paper: {
            style: {
              maxHeight: 4.5*ITEM_HEIGHT,
              minWidth: '25%'
            }
          }
        }}
      >
        <Typography
          sx={{fontWeight: 700}}
        >
          Group Bar Chart by
        </Typography>
        <Typography
          sx={{fontWeight: 700}}
          color='text.secondary'
        >
          (x-axis)
        </Typography>
        <Divider/>
        {listView.listview_columns.map((column) => barChartMenuElement(column))}
        <Divider/>
        <Box sx={{display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: 1}}
        >
          <Button
            size="small"
            onClick={() => submitBarChart()}
            color={'primary'}
            variant={'contained'}
            disabled={barChartGroups.length !== 1}
          >
            <CheckCircleIcon fontSize="small"/>
          </Button>
          <Button
            size="small"
            onClick={() => resetBarChart()}
            color={'secondary'}
            variant={'contained'}
          >
            <RestartAltIcon fontSize="small"/>
          </Button>
        </Box>
      </Menu>
  );

};
